import {
	Component,
	ElementRef,
	EventEmitter,
	HostListener,
	Input,
	OnInit,
	Output, QueryList,
	Renderer2,
	ViewChild, ViewChildren,
	OnChanges, SimpleChanges
} from '@angular/core';
import { InputComponent } from '../input/input.component';
import { Subject, Observable, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
	selector: 'cv-multiselect-search',
	templateUrl: './multiselect-search.component.html',
	styleUrls: ['./multiselect-search.component.scss']
})
export class MultiselectSearchComponent implements OnInit, OnChanges {
	@Input() singleSelectMode: boolean;
	@Input() required: boolean;
	@Input() label: string;
	@Input() disabled: boolean;
	@Input() width: string;
	@Input() valid = true;
	@Input() options: Array<any>;
	@Input() values: Array<any>;
	@Input() optionValue: string;
	@Input() optionText: string;
	@Input() placeholder: string;
	@Input() getOptionsFunction: (arg1: string) => Observable<any>;
	@Output() valuesChange: EventEmitter<any> = new EventEmitter<any>();
	@Output() openMenu: EventEmitter<any> = new EventEmitter<any>();
	@Output() closeMenu: EventEmitter<any> = new EventEmitter<any>();
	@ViewChild('dropdownLabel', { static: true }) dropdownLabel: ElementRef<any>;
	@ViewChild('dropdownButton', { static: true }) dropdownButton: ElementRef<any>;
	@ViewChild('dropdownMenu', { static: true }) dropdownMenu: ElementRef<any>;
	@ViewChild('cvInput', { static: true }) cvInput: InputComponent;
	@ViewChildren('dropdownOption') dropdownOptions: QueryList<any>;
	isOpen: boolean;
	currentValues: Array<any>;
	randomID: number;
	textFilter: string;
	filteredOptions: Array<any>;
	defaultOptions: Array<any>;
	loading: boolean;
	private filterChanged: Subject<string> = new Subject<string>();
	private eventFilterChanged: EventEmitter<string> = new EventEmitter<string>();
	private subscription: Subscription;
	private readonly debounceTime = 700;

	constructor(
		private renderer: Renderer2
	) {
		this.options = [];
		this.values = [];
		this.width = '100%';
		this.isOpen = false;
		this.randomID = Math.random() * 10000;
		this.loading = false;
	}

	ngOnInit(): void {
		this.subscription = this.filterChanged.pipe(debounceTime(this.debounceTime)).subscribe((r) => {
			this.options = [...r];
			this.filterOptions();
			this.loading = false;
		});

		this.eventFilterChanged
		.pipe(
			debounceTime(this.debounceTime),
			distinctUntilChanged()
		)
		.subscribe((event: any) => {
			this.getOptionsFunction(event)?.pipe().subscribe(r => {
				this.filterChanged?.next(r);
			});
		});

		if (this.singleSelectMode && this.values && this.values.length) {
			this.currentValues = [this.values[0]];
		} else {
			this.currentValues = [...this.values];
		}
		this.filteredOptions = [...this.options];
		this.filterOptions();
		this.defaultOptions = [...this.options];
	}

	/**
	 * Detect changes on values variable in order to option selected
	 * @param changes 
	 */
	ngOnChanges(changes: SimpleChanges) {
		if (changes['values']) {
			this.values = [...changes['values']?.currentValue];

			if (this.singleSelectMode && this.values && this.values.length) {
				this.currentValues = [this.values[0]];
			} else {
				this.currentValues = [...this.values];
			}
		}

		if (changes['options'] && changes['options'].currentValue) {
			this.options = [...changes['options'].currentValue];
			this.defaultOptions = [...this.options];
			this.filteredOptions = [...this.options];
			this.filterOptions();
		}
	}

	/**
	 * Closes the dropdown menu is user clicks outside.
	 * @param $event: any
	 */
	@HostListener('document:click', ['$event'])
	onClickOutside($event: any): void {
		if (!this.dropdownButton.nativeElement.contains($event.target)
			&& !this.dropdownMenu.nativeElement.contains($event.target)) {
			this.toggleMenu(true);
		}
	}

	/**
	 * Emits the valuesChange event.
	 */
	onValuesChange(): void {
		if (this.singleSelectMode) {
			this.valuesChange.emit(this.currentValues.length ? this.currentValues[0] : null);
		} else {
			this.valuesChange?.emit(this.currentValues);
		}
	}

	/**
	 * Toggles(Display or hide) the dropdown menu. If it's display action it calculates the position based
	 * on the button position.
	 * @param forceClose: if true then closes the dropdown menu.
	 */
	toggleMenu(forceClose: boolean = false): void {
		const openTheMenu = forceClose ? false : !this.isOpen;
		if (openTheMenu) {
			this.openDropdownMenu();
		} else {
			this.renderer.setStyle(this.dropdownMenu.nativeElement, 'display', 'none');
			this.isOpen = false;
			this.closeMenu.emit();
		}
	}

	/**
	 * Open dropdown menu.
	 */
	openDropdownMenu(): void {
		// if it's already open do nothing.
		if (!this.isOpen) {
			const top = this.dropdownButton.nativeElement.offsetHeight +
				this.dropdownLabel.nativeElement.offsetHeight + 10;
			const left = this.dropdownButton.nativeElement.offsetLeft;
			this.renderer.setStyle(this.dropdownMenu.nativeElement, 'top', `${top}px`);
			this.renderer.setStyle(this.dropdownMenu.nativeElement, 'left', `${left}px`);
			this.renderer.setStyle(this.dropdownMenu.nativeElement, 'display', 'block');
			this.isOpen = true;
			this.focusOnSearchInput();
			this.openMenu.emit();
		}
	}

	/**
	 * On search filter
	 * @param $event
	 */
	onFilter($event: any): void {
		if ($event.$event && ($event.$event.code === 'Escape' || $event.$event.key === 'Escape')) {
			this.toggleMenu(true);
		}

		// if it's key arrow down event focus on first menu option if available.
		if ($event.$event && ($event.$event.code === 'ArrowDown'
			|| $event.$event.key === 'ArrowDown') && this.isOpen) {
			this.focusOnFirstOption();
		} else {
			this.textFilter = $event.value;

			if (this.getOptionsFunction && $event.value.trim().length >= 3) {
				this.loading = true;
				this.eventFilterChanged.emit($event.value);
			} else {

				// If not filter and options empty set default settings
				if (!this.textFilter.trim().length) {
					this.options = [...this.defaultOptions];
				}

				this.filterOptions();
			}
		}
	}

	/**
	 * Allow tab navigation on Arrow Up/Down/Enter key events.
	 */
	tabNavigationOnArrowKeyEvents($event: any, option: any, index: number): void {
		if (($event.code === 'ArrowDown' || $event.key === 'ArrowDown')
			&& $event.target && $event.target.nextElementSibling) {
			$event.target.nextElementSibling.focus();
		} else if ($event.code === 'ArrowUp' || $event.key === 'ArrowUp' &&
			$event.target && $event.target.previousElementSibling) {
			if (index === 0) {
				this.focusOnSearchInput();
			} else {
				$event.target.previousElementSibling.focus();
			}
		} else if ($event.code === 'Enter' || $event.key === 'Enter') {
			this.toggleOption(true, option);
			this.focusOnSearchInput();
		} else if ($event.code === 'Escape' || $event.key === 'Escape') {
			this.toggleMenu(true);
		}
	}

	/**
	 * Filter options based on text filter and current selected options.
	 */
	filterOptions(): void {
		this.filteredOptions = this.filterSelectedOptions();
		if (this.textFilter && this.textFilter.length) {
			this.filteredOptions = this.filteredOptions
			?.filter(item => item[this.optionText]?.toLowerCase().includes(this.textFilter.toLowerCase()));
		} else {
			this.filteredOptions = [...this.filteredOptions];
		}
	}

	/**
	 * Returns if option is currently selected.
	 * @param option: any
	 */
	isOptionSelected(option: any): boolean {
		const match = this.currentValues.find(value => value[this.optionValue] === option[this.optionValue]);
		return match;
	}

	/**
	 * Add or remove the option into the current selected values array.
	 * @param $event: any
	 * @param option: any
	 */
	toggleOption(selected: boolean, option: any, $event: any = null): void {
		if ($event) {
			$event.stopPropagation();
		}
		if (selected) {
			if (this.singleSelectMode) {
				this.currentValues = [option];
			} else {
				this.currentValues.push(option);
			}
		} else {
			const index = this.currentValues.findIndex(value => value[this.optionValue] === option[this.optionValue]);
			if (index > -1) {
				this.currentValues.splice(index, 1);
			}
		}
		this.filterOptions();
		this.onValuesChange();
	}

	/**
	 * Sets values to empty array.
	 */
	clearValues(): void {
		this.currentValues.splice(0);
		this.onValuesChange();
	}

	/**
	 * Returns true if component value is valid
	 */
	isValid(): boolean {
		if (this.required) {
			return this.currentValues !== undefined
				&& this.currentValues !== null
				&& this.currentValues.length > 0;
		}
		return true;
	}

	/**
	 * Focus on the search input element.
	 * @private
	 */
	private focusOnSearchInput(): void {
		this.cvInput.inputElement.nativeElement.focus();
	}

	/**
	 * Set the user focus on the first dropdown option
	 */
	private focusOnFirstOption(): void {
		if (this.dropdownOptions.first
			&& this.dropdownOptions.first.nativeElement) {
			this.dropdownOptions.first.nativeElement.focus();
		}
	}

	/**
	 * Filters (excludes) selected options from filtered list.
	 */
	private filterSelectedOptions(): Array<any> {
		return this.options?.filter(item => {
			return this.currentValues
				.filter(value => value[this.optionValue] === item[this.optionValue]).length === 0;
		});
	}
}